/**
 * SA demo-tenant provision form (B-11; provision→status flow 2026-06-12;
 * #404 sales-sandbox alignment 2026-07-13).
 *
 * Route: /demos/new
 *
 * Sales flow (Prospect Sandboxes MVP):
 *   - Type a prospect / account name → a safe slug is derived live
 *     (slugified, validated against ^[a-z0-9][a-z0-9-]*[a-z0-9]$). The slug
 *     stays editable; once the SA edits it by hand, the name no longer
 *     overwrites it.
 *   - The admin email defaults to the standard demo login
 *     (`demo@certainhr.ai`). Per PR #404 every sandbox uses that same email +
 *     the fixed known DEMO_ADMIN_PASSWORD (unique-per-tenant email constraint),
 *     with no forced first-login reset — so the login is copy-paste ready and
 *     survives every reset.
 *   - A COGS hint reminds sales that each provision fires real AI calls
 *     (rank-one + top-8 auto-eval) and burns AI credits — don't bulk-create.
 *
 * On submit the form fires the provision run into `useDemoProvisionStore`
 * (which holds the long synchronous POST outside this component) and navigates
 * IMMEDIATELY to /demos/{slug}, where DemoDetail renders the Provisioning →
 * Ready/Failed state machine and surfaces the copy-paste login block.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/admin/PageHeader";
import { useDemoProvisionStore } from "@/stores/useDemoProvisionStore";

const CONFIRM_TOKEN = "PROVISION DEMO";
// Canon regex from app/schemas/admin/tenants.py:26
const SLUG_RE = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;
// Per PR #404 — every sandbox shares this admin login (email is unique
// per-tenant, so the same address is reused across sandboxes) plus the fixed
// known DEMO_ADMIN_PASSWORD. The SA never types or sees a password here.
const DEMO_ADMIN_EMAIL = "demo@certainhr.ai";

function isValidSlug(slug: string): boolean {
  return slug.length >= 1 && slug.length <= 50 && (slug.length === 1 || SLUG_RE.test(slug));
}

/** Derive a safe slug from a free-text prospect name. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "")
    .slice(0, 50)
    .replace(/-+$/, "");
}

export default function DemoProvision(): JSX.Element {
  const navigate = useNavigate();
  const startProvision = useDemoProvisionStore((s) => s.startProvision);

  const [prospectName, setProspectName] = useState("");
  const [slug, setSlug] = useState("");
  // Once the SA edits the slug by hand, stop auto-deriving it from the name.
  const [slugTouched, setSlugTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState(DEMO_ADMIN_EMAIL);
  const [adminFirstName, setAdminFirstName] = useState("Demo");
  const [adminLastName, setAdminLastName] = useState("Admin");
  const [description, setDescription] = useState("");
  const [confirmInput, setConfirmInput] = useState("");

  const slugValid = isValidSlug(slug);
  const confirmOk = confirmInput === CONFIRM_TOKEN;
  const submitEnabled = slugValid && confirmOk && adminEmail.length > 0;

  function onProspectNameChange(value: string) {
    setProspectName(value);
    // Auto-derive the slug from the name until the SA takes manual control.
    if (!slugTouched) setSlug(slugify(value));
  }

  function onSlugChange(value: string) {
    setSlugTouched(true);
    setSlug(value.toLowerCase());
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!submitEnabled) return;
    // Fire-and-navigate: the store owns the in-flight POST so the run survives
    // this component unmounting. We do NOT await — navigation is immediate.
    void startProvision({
      slug,
      admin_email: adminEmail,
      admin_first_name: adminFirstName,
      admin_last_name: adminLastName,
      description: description || prospectName || undefined,
      confirm: CONFIRM_TOKEN,
    });
    navigate(`/demos/${slug}`);
  }

  const inputClass =
    "w-full rounded border border-brand-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary";
  const labelClass =
    "mb-1 block text-sm font-medium text-text-secondary dark:text-brand-100/70";

  return (
    <div className="max-w-2xl space-y-5">
      <PageHeader title="New prospect sandbox" />

      <p className="text-sm text-text-secondary dark:text-brand-100/60">
        Spins up an isolated demo tenant seeded with JDs, ranked candidates
        (resumes + headshots), and starter credits. Hand the prospect the
        sandbox&apos;s own <code className="text-xs">/{"{slug}"}/login</code> URL
        and the standard demo credentials.
      </p>

      {/* COGS / AI-credits hint — provisioning fires real AI calls (rank-one +
          top-8 auto-eval), so each sandbox costs credits. Don't bulk-create. */}
      <div
        className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200"
        data-testid="demo-cogs-hint"
      >
        Heads up: each sandbox runs real AI on creation (rank-one +
        top-8 auto-eval) and <strong>consumes AI credits</strong>. Provision one
        per active prospect — please don&apos;t spin up sandboxes in bulk.
      </div>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="demo-provision-form">
        {/* Prospect / account name → derives the slug. */}
        <div>
          <label className={labelClass}>
            Prospect / account name{" "}
            <span className="font-normal text-text-secondary">(derives the slug)</span>
          </label>
          <input
            type="text"
            value={prospectName}
            onChange={(e) => onProspectNameChange(e.target.value)}
            placeholder="Acme Talent Partners"
            maxLength={120}
            className={inputClass}
            data-testid="demo-prospect-name"
          />
        </div>

        {/* Slug field with live validation */}
        <div>
          <label className={labelClass}>
            Slug{" "}
            <span className="text-text-secondary font-normal">
              (e.g. <code className="text-xs">tyler-agency</code>)
            </span>
          </label>
          <input
            required
            type="text"
            name="slug"
            id="slug"
            value={slug}
            onChange={(e) => onSlugChange(e.target.value)}
            className={`${inputClass} ${slug && !slugValid ? "border-red-400 dark:border-red-500/60" : ""}`}
            placeholder="acme"
            maxLength={50}
            data-testid="demo-slug-input"
          />
          {slug && !slugValid ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              Slug must be 1–50 chars, lowercase letters, digits, or hyphens, and not
              start/end with a hyphen.
            </p>
          ) : null}
          {/* Permanent-reservation warning — non-dismissable per design §1 */}
          <div
            className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200"
            data-testid="demo-reservation-warning"
          >
            Provisioning{slug ? ` "${slug}"` : " this slug"} reserves it from
            real-tenant onboarding. Sandboxes don&apos;t expire automatically —
            DELETE this one when the deal closes to free the slug.
          </div>
        </div>

        <div>
          <label className={labelClass}>Admin email</label>
          <input
            required
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            className={inputClass}
            data-testid="demo-admin-email"
          />
          <p className="mt-1 text-xs text-text-secondary dark:text-brand-100/50">
            Defaults to the shared demo login. Every sandbox uses{" "}
            <code className="text-xs">{DEMO_ADMIN_EMAIL}</code> + the fixed demo
            password (no forced reset), so the login is copy-paste ready.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>First name</label>
            <input
              required
              value={adminFirstName}
              onChange={(e) => setAdminFirstName(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Last name</label>
            <input
              required
              value={adminLastName}
              onChange={(e) => setAdminLastName(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>
            Description{" "}
            <span className="font-normal text-text-secondary">(optional)</span>
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Tyler agency demo, AEDT compliance walkthrough, …"
            maxLength={500}
            className={inputClass}
          />
          <p className="mt-1 text-xs text-text-secondary dark:text-brand-100/50">
            Saved to audit log only — not visible in the app.
          </p>
        </div>

        <div>
          <label className={labelClass}>
            Type{" "}
            <code className="rounded bg-brand-100 px-1 dark:bg-white/10">
              {CONFIRM_TOKEN}
            </code>{" "}
            to confirm
          </label>
          <input
            required
            name="confirm"
            id="confirm"
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder={CONFIRM_TOKEN}
            className={inputClass}
            data-testid="demo-confirm-input"
          />
        </div>

        <button
          type="submit"
          disabled={!submitEnabled}
          className="rounded bg-accent-primary-hover px-4 py-2 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
          data-testid="demo-submit"
        >
          Provision sandbox
        </button>
        <p className="text-xs text-text-secondary dark:text-brand-100/60">
          You&apos;ll land on the sandbox&apos;s page and watch it provision live —
          takes about a minute while the AI ranks each candidate against its JD.
        </p>
      </form>
    </div>
  );
}
